import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { DefaultTheme, ThemeProvider as StyledThemeProvider } from "styled-components";
import { Theme as TamaguiTheme } from "tamagui";
import {
    colorTokens,
    shadowTokens,
    typographyTokens,
    spacingTokens,
    borderRadiusTokens,
    zIndexTokens,
    semanticColorTokens,
    chartColorTokens,
    interactionTokens,
    gradientTokens
} from "../config/designTokens";

// ============================================
// Types
// ============================================

export type ThemeMode = "light" | "dark" | "system";

// ... existing types ...

interface ThemeContextValue {
    themeMode: ThemeMode;
    resolvedTheme: "light" | "dark";
    isDarkMode: boolean;
    colors: DefaultTheme;
    setThemeMode: (mode: ThemeMode) => void;
    toggleTheme: () => void;
    showMerchantLogo: boolean;
    setShowMerchantLogo: (show: boolean) => void;
}

// ============================================
// Constants
// ============================================

const STORAGE_KEY = "greenback_theme_mode";
const SHOW_MERCHANT_LOGO_KEY = "show_merchant_logo";

// ============================================
// Context
// ============================================

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================
// Helper Functions
// ============================================

const getStoredThemeMode = (): ThemeMode => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "light" || stored === "dark" || stored === "system") {
            return stored;
        }
    } catch (e) {
        // localStorage not available
    }
    return "system";
};

const getSystemPreference = (): "light" | "dark" => {
    if (typeof window !== "undefined" && window.matchMedia) {
        const mql = window.matchMedia("(prefers-color-scheme: dark)");
        return mql?.matches ? "dark" : "light";
    }
    return "light";
};

// ============================================
// Theme Bridge — reads colorTokens directly, feeds styled-components
// ============================================

interface ThemeBridgeProps {
    isDarkMode: boolean;
    children: ReactNode;
}

const ThemeBridge: React.FC<ThemeBridgeProps> = ({ isDarkMode, children }) => {
    // Read directly from colorTokens (plain hex values) to avoid CSS variable
    // indirection on web — Tamagui's .val returns `var(--t-color-X)` strings,
    // not resolved hex, so styled-components would receive stale CSS vars.
    const ct  = isDarkMode ? colorTokens.dark  : colorTokens.light;
    const sem = isDarkMode ? semanticColorTokens.dark : semanticColorTokens.light;

    const scTheme: DefaultTheme = {
        isDarkMode,
        background: ct.background,
        text:       ct.text,
        brand:      ct.brand,
        border:     ct.border,
        semantic: {
            ...sem,
            success: sem.positive,
            error:   sem.negative,
        } as DefaultTheme["semantic"],
        neutral: ct.neutral,
        shadow: shadowTokens,
        typography: typographyTokens,
        spacing: spacingTokens,
        borderRadius: borderRadiusTokens,
        zIndex: zIndexTokens,
        chart: chartColorTokens,
        interactions: interactionTokens,
        gradient: isDarkMode ? gradientTokens.dark : gradientTokens.light,
    };

    return (
        <StyledThemeProvider theme={scTheme}>
            {children}
        </StyledThemeProvider>
    );
};

// ============================================
// Provider Component
// ============================================

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredThemeMode);
    const [systemPreference, setSystemPreference] = useState<"light" | "dark">(getSystemPreference);
    const [showMerchantLogo, setShowMerchantLogoState] = useState<boolean>(() => {
        try {
            const saved = localStorage.getItem(SHOW_MERCHANT_LOGO_KEY);
            return saved === "true";
        } catch (e) {
            return false;
        }
    });

    // Listen for system preference changes
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        if (!mediaQuery) return;

        const handleChange = (e: MediaQueryListEvent) => {
            setSystemPreference(e.matches ? "dark" : "light");
        };

        // Modern browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        }
        // Legacy/Test environments
        else if (mediaQuery.addListener) {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
    }, []);

    // Resolve the actual theme
    const resolvedTheme: "light" | "dark" = themeMode === "system" ? systemPreference : themeMode;

    // Get the color tokens for the current theme
    const colors = resolvedTheme === "dark" ? colorTokens.dark : colorTokens.light;

    // Persist theme mode to localStorage
    const setThemeMode = useCallback((mode: ThemeMode) => {
        setThemeModeState(mode);
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch (e) {
            // localStorage not available
        }
    }, []);

    // Persist merchant logo preference to localStorage
    const setShowMerchantLogo = useCallback((show: boolean) => {
        setShowMerchantLogoState(show);
        try {
            localStorage.setItem(SHOW_MERCHANT_LOGO_KEY, String(show));
        } catch (e) {
            // localStorage not available
        }
    }, []);

    // Toggle between light and dark (skipping system)
    const toggleTheme = useCallback(() => {
        setThemeMode(resolvedTheme === "light" ? "dark" : "light");
    }, [resolvedTheme, setThemeMode]);

    // Apply theme class to document
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", resolvedTheme);
        // Tamagui scopes its theme CSS variables to `.t_light` / `.t_dark`
        // ancestor classes, and TamaguiProvider stamps document.body with the
        // *static* defaultTheme class ("dark") on mount. React Native Web
        // portals (GBModal, GBDrawer, toasts, select sheets) render straight
        // into document.body — outside every in-tree <Theme> wrapper — so
        // without this sync those surfaces always resolve the dark palette
        // (e.g. a #111 modal panel behind light-theme black text). Keep body
        // stamped with the *active* theme instead. This passive effect runs
        // after TamaguiProvider's layout effect, so it wins on mount too.
        document.body.classList.remove("t_light", "t_dark");
        document.body.classList.add(`t_${resolvedTheme}`);
        document.body.style.backgroundColor = colors.background.default;
        document.body.style.color = colors.text.primary;
    }, [resolvedTheme, colors]);

    const value: ThemeContextValue = {
        themeMode,
        resolvedTheme,
        isDarkMode: resolvedTheme === "dark",
        colors: {
            ...colors,
            semantic: (resolvedTheme === "dark" ? semanticColorTokens.dark : semanticColorTokens.light) as any,
            isDarkMode: resolvedTheme === "dark",
            shadow: shadowTokens,
            typography: typographyTokens,
            spacing: spacingTokens,
            borderRadius: borderRadiusTokens,
            zIndex: zIndexTokens,
            chart: chartColorTokens,
            interactions: interactionTokens,
            gradient: (resolvedTheme === "dark" ? gradientTokens.dark : gradientTokens.light) as any,
        } as DefaultTheme,
        setThemeMode,
        toggleTheme,
        showMerchantLogo,
        setShowMerchantLogo,
    };

    return (
        <ThemeContext.Provider value={value}>
            <TamaguiTheme name={resolvedTheme}>
                <ThemeBridge isDarkMode={resolvedTheme === "dark"}>
                    {children}
                </ThemeBridge>
            </TamaguiTheme>
        </ThemeContext.Provider>
    );
};

// ============================================
// Hook
// ============================================

export const useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

// Helper hook to get just colors (for styled-components)
export const useColors = () => {
    const { colors } = useTheme();
    return colors;
};
